<style scoped>
.tool-row {
	cursor: pointer;
	transition: background-color 0.15s ease;
}
.tool-row:hover {
	background-color: rgba(13, 110, 253, 0.05);
}
.tool-icon {
	font-size: 1.5rem;
	width: 40px;
	text-align: center;
	color: #0d6efd;
}
</style>

<template>
	<div class="container mt-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<h2><i class="bi bi-map me-2"></i> MapVision Review</h2>
		</div>

		<div class="table-responsive">
			<table class="table table-hover align-middle">
				<thead class="table-light">
					<tr>
						<th style="width: 50px"></th>
						<th style="width: 140px">Area</th>
						<th>Description</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="tool in tools"
						:key="tool.route"
						class="tool-row"
						@click="router.push(tool.route)"
					>
						<td>
							<div class="tool-icon">
								<i :class="tool.icon"></i>
							</div>
						</td>
						<td class="fw-semibold">{{ tool.name }}</td>
						<td class="text-muted small">{{ tool.description }}</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div class="mt-5">
			<h3 class="mb-3"><i class="bi bi-database me-2"></i>Synchronized data</h3>

			<div v-if="loading" class="text-center py-4">
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>

			<div v-else-if="error" class="alert alert-danger">{{ error }}</div>

			<div v-else-if="sections.length === 0" class="text-muted">
				No synchronized data types available.
			</div>

			<section v-for="section in sections" :key="section.key" class="mb-4">
				<h4 class="mb-2">{{ section.label }}</h4>
				<div class="table-responsive">
					<table class="table table-hover align-middle">
						<thead class="table-light">
							<tr>
								<th>Type</th>
								<th style="width: 220px">Table</th>
								<th>Description</th>
								<th class="text-end" style="width: 120px">Rows</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="type in section.types"
								:key="type.name"
								class="tool-row"
								@click="router.push(`/_/review/mapvision/type/${type.name}`)"
							>
								<td class="fw-semibold">{{ type.label }}</td>
								<td>
									<code>{{ type.table }}</code>
								</td>
								<td class="text-muted small">{{ type.description }}</td>
								<td class="text-end">{{ type.row_count.toLocaleString() }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</section>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { apiClient } from "@/client";
import type { MapvisionReviewCategory, MapvisionReviewType } from "@/type/api";

const router = useRouter();

interface Tool {
	name: string;
	icon: string;
	description: string;
	route: string;
}

const tools: Tool[] = [
	{
		name: "Map",
		icon: "bi bi-map",
		description: "Review MapVision sites on an interactive map.",
		route: "/_/review/mapvision/map",
	},
	{
		name: "Activity Card",
		icon: "bi bi-card-list",
		description: "Review MapVision activity card data.",
		route: "/_/review/mapvision/activity-card",
	},
];

const loading = ref(false);
const error = ref("");
const types = ref<MapvisionReviewType[]>([]);

// Catalog grouped by category, in display order; empty categories are hidden.
const sections = computed(() => {
	const order: { key: MapvisionReviewCategory; label: string }[] = [
		{ key: "layers", label: "Layers" },
		{ key: "lists", label: "Lists" },
		{ key: "master", label: "Master reference" },
	];
	return order
		.map((section) => ({
			...section,
			types: types.value.filter((t) => t.category === section.key),
		}))
		.filter((section) => section.types.length > 0);
});

async function loadTypes() {
	loading.value = true;
	error.value = "";
	try {
		types.value = await apiClient.JSONGet<MapvisionReviewType[]>(
			"/api/mapvision/type",
		);
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to load synchronized data";
	} finally {
		loading.value = false;
	}
}

onMounted(() => {
	void loadTypes();
});
</script>
